import { useEffect, useRef, useState } from 'react';

/**
 * Hook for managing voice announcements for flight status changes
 * Uses ResponsiveVoice API for text-to-speech
 * 
 * @param {Array} arrivals - Array of arrival flights
 * @param {Array} departures - Array of departure flights
 * @param {string} category - Current category filter ('domestic' or 'international')
 */

// Only log in development mode - these will be completely removed in production builds
const log = process.env.NODE_ENV === 'development' 
  ? (...args) => console.log(...args)
  : () => {}; // Empty function in production - will be removed by minifier

const logError = process.env.NODE_ENV === 'development'
  ? (...args) => console.error(...args)
  : () => {}; // Empty function in production - will be removed by minifier

const useVoiceAnnouncements = (arrivals, departures, category) => {
  const previousFlightsRef = useRef(new Map());
  const announcementQueueRef = useRef([]);
  const announcedStatusesRef = useRef(new Set()); // Track which announcements have already been made
  const [isPlaying, setIsPlaying] = useState(false);
  const isPlayingRef = useRef(false); // Ref to track playing state for queue processing
  const [userInteracted, setUserInteracted] = useState(false);
  const userInteractedRef = useRef(false); // Ref to track user interaction for closures
  const isInitialLoadRef = useRef(true);
  const currentCategoryRef = useRef(category);
  const audioStartRef = useRef(null);
  const audioEndRef = useRef(null);
  const audioShotRef = useRef(null);
  const playAnnouncementRef = useRef(null);
  const processNextRef = useRef(null);

  // Voice configuration - change voice type here
  const VOICE_TYPE = 'US English Female'; // Change to: 'UK English Female', 'US English Male', etc.
  // See available voices: https://responsivevoice.org/text-to-speech-languages/

  // Initialize audio elements and handle user interaction
  useEffect(() => {
    // Create audio elements with error handling
    audioStartRef.current = new Audio('/announce-start.mp3');
    audioEndRef.current = new Audio('/annouce-end.mp3'); // Note: filename has typo (annouce not announce)
    audioShotRef.current = new Audio('/annouce-shot.mp3'); // Note: filename has typo (annouce not announce)

    // Preload audio files
    audioStartRef.current.preload = 'auto';
    audioEndRef.current.preload = 'auto';
    audioShotRef.current.preload = 'auto';

    // Add error handlers to detect loading issues
    const handleAudioError = (audioName, error) => {
      logError(`[VOICE] Audio error for ${audioName}:`, error);
      // Check if it's a network/loading error
      if (error.target && error.target.error) {
        const audioError = error.target.error;
        if (audioError.code === audioError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
          logError(`[VOICE] Audio file not found or not supported: ${audioName}`);
        } else if (audioError.code === audioError.MEDIA_ERR_NETWORK) {
          logError(`[VOICE] Network error loading audio: ${audioName}`);
        }
      }
    };

    audioStartRef.current.addEventListener('error', (e) => handleAudioError('announce-start.mp3', e));
    audioEndRef.current.addEventListener('error', (e) => handleAudioError('annouce-end.mp3', e));
    audioShotRef.current.addEventListener('error', (e) => handleAudioError('annouce-shot.mp3', e));

    // Handle user interaction to enable autoplay
    const handleUserInteraction = () => {
      setUserInteracted(true);
      userInteractedRef.current = true; // Update ref for closures
      // Try to play and immediately pause to "unlock" audio
      if (audioStartRef.current) {
        audioStartRef.current.play().catch(() => {
          // Ignore errors - this is just to unlock audio
        }).then(() => {
          if (audioStartRef.current) {
            audioStartRef.current.pause();
            audioStartRef.current.currentTime = 0;
          }
        });
      }
    };

    // Listen for any user interaction
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });
    document.addEventListener('touchstart', handleUserInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, []);

  // Cleanup on unmount (e.g., page refresh) - stop all announcements and clear queue
  useEffect(() => {
    return () => {
      log('[VOICE] Component unmounting - stopping announcements and clearing queue');
      // Stop ResponsiveVoice if it's speaking
      if (window.responsiveVoice) {
        try {
          window.responsiveVoice.cancel();
        } catch (error) {
          // Ignore errors
        }
      }
      // Stop all audio
      if (audioStartRef.current) {
        audioStartRef.current.pause();
        audioStartRef.current.currentTime = 0;
      }
      if (audioEndRef.current) {
        audioEndRef.current.pause();
        audioEndRef.current.currentTime = 0;
      }
      if (audioShotRef.current) {
        audioShotRef.current.pause();
        audioShotRef.current.currentTime = 0;
      }
      // Clear queue, announced statuses, and reset state
      // eslint-disable-next-line react-hooks/exhaustive-deps
      announcementQueueRef.current = [];
      // eslint-disable-next-line react-hooks/exhaustive-deps
      announcedStatusesRef.current.clear();
      setIsPlaying(false);
      isPlayingRef.current = false;
    };
  }, []);

  // Generate announcement message based on status
  const generateAnnouncement = (flight, status, isArrival = false) => {
    const flightNumber = flight.flightNumber || 'Unknown';
    const terminal = flight.terminal || 'Unknown';
    const statusUpper = status?.toUpperCase() || '';

    // For arrivals, use origin; for departures, use destination
    const location = isArrival 
      ? (flight.origin || 'Unknown')
      : (flight.destination || 'Unknown');

    // Split flight number for clearer pronunciation (e.g., "Q2 123" -> "Q 2 1 2 3")
    const flightNumberParts = flightNumber.split(/\s+/);
    const flightCode = flightNumberParts[0]?.split('').join(' ') || '';
    const flightNum = flightNumberParts[1]?.split('').join(' ') || '';
    const formattedFlightNumber = `${flightCode} ${flightNum}`.trim();

    if (statusUpper.includes('BOARDING')) {
      // Only for departures
      if (!isArrival) {
        return `Flight Number ${formattedFlightNumber} to ${location} is ready for boarding. Please proceed to terminal ${terminal}`;
      }
    } else if (statusUpper.includes('FINAL CALL')) {
      // Only for departures
      if (!isArrival) {
        return `Final call for Flight Number ${formattedFlightNumber} to ${location}. Please proceed immediately to terminal ${terminal}`;
      }
    } else if (statusUpper.includes('CANCELLED')) {
      // For both arrivals and departures
      return `Flight Number ${formattedFlightNumber} ${location} has been cancelled. Please contact your airline for assistance`;
    } else if (statusUpper.includes('LANDED')) {
      // Only for arrivals
      if (isArrival) {
        return `Flight Number ${formattedFlightNumber} from ${location} has landed at terminal ${terminal}`;
      }
    } else if (statusUpper.includes('DELAYED')) {
      // Only for arrivals
      if (isArrival) {
        const estimatedTime = flight.estimatedTime || 'TBA';
        return `Flight Number ${formattedFlightNumber} from ${location} has been delayed. New estimated arrival time is ${estimatedTime}. Please proceed to terminal ${terminal}`;
      }
    }

    return null; // No announcement for other statuses
  };

  // Safe audio play function that handles autoplay errors and waits for completion
  const playAudio = async (audioElement, retryOnBlock = true) => {
    if (!audioElement) {
      logError('[VOICE] playAudio: audioElement is null');
      return Promise.resolve();
    }
    
    return new Promise((resolve) => {
      const attemptPlay = (isRetry = false) => {
        try {
          // Check if audio is ready to play
          if (audioElement.readyState >= 2) { // HAVE_CURRENT_DATA or higher
            // Audio is loaded, proceed with playback
            audioElement.currentTime = 0;
            audioElement.onended = resolve;
            audioElement.onerror = () => {
              logError('[VOICE] Audio playback error');
              resolve(); // Resolve on error to continue
            };
            
            audioElement.play()
              .then(() => {
                // Audio started successfully, verify it's actually playing
                log('[VOICE] Audio play() promise resolved');
                // Check after a short delay if audio is actually playing
                setTimeout(() => {
                  if (!audioElement.paused && !audioElement.ended) {
                    log('[VOICE] Audio confirmed playing');
                  } else {
                    logError('[VOICE] WARNING: Audio play() succeeded but element is paused or ended');
                  }
                }, 50);
                // Will resolve when onended fires
              })
              .catch((error) => {
                // Autoplay was prevented - this is expected until user interacts
                if (error.name === 'NotAllowedError') {
                  if (retryOnBlock && !isRetry) {
                    // Wait for user interaction, then retry - keep trying for up to 2 seconds
                    log('[VOICE] Audio autoplay prevented, waiting for user interaction...');
                    let waitCount = 0;
                    const maxWaits = 40; // 2 seconds total (40 * 50ms)
                    const waitInterval = setInterval(() => {
                      waitCount++;
                      // Check userInteractedRef - always has latest value
                      if (userInteractedRef.current) {
                        clearInterval(waitInterval);
                        log('[VOICE] User interaction detected, retrying audio...');
                        setTimeout(() => attemptPlay(true), 100);
                      } else if (waitCount >= maxWaits) {
                        clearInterval(waitInterval);
                        // Even if no interaction, try one more time
                        log('[VOICE] Max wait reached, attempting to play audio anyway...');
                        setTimeout(() => attemptPlay(true), 100);
                      }
                    }, 50);
                  } else {
                    // On retry, if still blocked, set up onended handler and keep trying
                    audioElement.onended = resolve;
                    audioElement.onerror = () => {
                      logError('[VOICE] Audio playback error on retry');
                      resolve();
                    };
                    
                    // Check if audio is actually playing, if not keep trying
                    const checkPlaying = setInterval(() => {
                      if (!audioElement.paused) {
                        // Audio is playing!
                        clearInterval(checkPlaying);
                        log('[VOICE] Audio started playing on retry');
                        // onended handler will resolve when done
                      } else if (audioElement.readyState >= 2) {
                        // Audio is loaded, try playing
                        audioElement.play()
                          .then(() => {
                            clearInterval(checkPlaying);
                            log('[VOICE] Audio started playing on retry attempt');
                          })
                          .catch(() => {
                            // Still blocked, will keep trying
                          });
                      }
                    }, 100);
                    
                    // Fallback: if still not playing after 3 seconds, wait for duration and resolve
                    setTimeout(() => {
                      clearInterval(checkPlaying);
                      if (audioElement.paused) {
                        log('[VOICE] Audio did not play after retry - waiting for duration then continuing');
                        setTimeout(() => resolve(), 1500);
                      }
                    }, 3000);
                  }
                } else {
                  logError('[VOICE] Audio play error:', error);
                  resolve(); // Other errors, just resolve
                }
              });
          } else {
            // Audio not ready yet, wait for it to load
            log('[VOICE] Audio not ready, waiting for canplay event');
            const handleCanPlay = () => {
              audioElement.removeEventListener('canplay', handleCanPlay);
              audioElement.removeEventListener('error', handleError);
              audioElement.currentTime = 0;
              audioElement.onended = resolve;
              audioElement.onerror = () => {
                logError('[VOICE] Audio playback error');
                resolve();
              };
              
              audioElement.play()
                .then(() => {
                  log('[VOICE] Audio started playing (after loading)');
                })
                .catch((error) => {
                  if (error.name === 'NotAllowedError') {
                    if (retryOnBlock && !isRetry) {
                      log('[VOICE] Audio autoplay prevented, waiting for user interaction...');
                      let waitCount = 0;
                      const maxWaits = 40; // 2 seconds total (40 * 50ms)
                      const waitInterval = setInterval(() => {
                        waitCount++;
                        if (userInteractedRef.current) {
                          clearInterval(waitInterval);
                          log('[VOICE] User interaction detected, retrying audio...');
                          setTimeout(() => attemptPlay(true), 100);
                        } else if (waitCount >= maxWaits) {
                          clearInterval(waitInterval);
                          log('[VOICE] Max wait reached, attempting to play audio anyway...');
                          setTimeout(() => attemptPlay(true), 100);
                        }
                      }, 50);
                    } else {
                      // On retry, if still blocked, set up onended handler and keep trying
                      audioElement.onended = resolve;
                      audioElement.onerror = () => {
                        logError('[VOICE] Audio playback error on retry');
                        resolve();
                      };
                      
                      const checkPlaying = setInterval(() => {
                        if (!audioElement.paused) {
                          clearInterval(checkPlaying);
                          log('[VOICE] Audio started playing on retry');
                        } else if (audioElement.readyState >= 2) {
                          audioElement.play()
                            .then(() => {
                              clearInterval(checkPlaying);
                              log('[VOICE] Audio started playing on retry attempt');
                            })
                            .catch(() => {
                              // Still blocked, will keep trying
                            });
                        }
                      }, 100);
                      
                      setTimeout(() => {
                        clearInterval(checkPlaying);
                        if (audioElement.paused) {
                          log('[VOICE] Audio did not play after retry - waiting for duration then continuing');
                          setTimeout(() => resolve(), 1500);
                        }
                      }, 3000);
                    }
                  } else {
                    logError('[VOICE] Audio play error:', error);
                    resolve();
                  }
                });
            };
            
            const handleError = () => {
              audioElement.removeEventListener('canplay', handleCanPlay);
              audioElement.removeEventListener('error', handleError);
              logError('[VOICE] Audio failed to load');
              resolve(); // Resolve on error to continue
            };
            
            audioElement.addEventListener('canplay', handleCanPlay, { once: true });
            audioElement.addEventListener('error', handleError, { once: true });
            
            // Load the audio if not already loading
            if (audioElement.readyState === 0) {
              audioElement.load();
            }
            
            // Timeout after 3 seconds if audio doesn't load
            setTimeout(() => {
              audioElement.removeEventListener('canplay', handleCanPlay);
              audioElement.removeEventListener('error', handleError);
              logError('[VOICE] Audio loading timeout');
              resolve(); // Resolve on timeout to continue
            }, 3000);
          }
        } catch (error) {
          logError('[VOICE] Error in playAudio:', error);
          resolve(); // Resolve on any error to continue
        }
      };
      
      attemptPlay(false);
    });
  };

  // Handle voice end - play end sound and process next
  const handleVoiceEnd = () => {
    log('[VOICE] handleVoiceEnd called - clearing playing state and processing next');
    setIsPlaying(false);
    isPlayingRef.current = false;
    
    // Process next in queue (with a small delay to allow state to update)
    const processNext = () => {
      log('[VOICE] Processing next item in queue');
      if (processNextRef.current) {
        setTimeout(processNextRef.current, 200);
      }
    };
    
    // Try to play end sound, but don't block on it
    if (audioEndRef.current) {
      // Set up handlers
      audioEndRef.current.onended = processNext;
      audioEndRef.current.onerror = processNext;
      
      // Try to play - if autoplay is blocked, this resolves immediately
      // In that case, we still want to process next, so we do it after a short delay
      playAudio(audioEndRef.current)
        .then(() => {
          // If autoplay was blocked, playAudio resolves immediately
          // Give handlers a chance to fire, but also set a fallback
          setTimeout(() => {
            // If we get here and handlers haven't fired, process next anyway
            processNext();
          }, 300);
        })
        .catch(() => {
          // If playAudio fails, still process next
          processNext();
        });
    } else {
      // No end audio, just process next
      processNext();
    }
  };

  // Play a single announcement
  playAnnouncementRef.current = async (announcement) => {
    const { flight, status, isCancelled, isArrival = false } = announcement;

    try {
      log(`[VOICE] Starting announcement for ${flight.flightNumber}, status: ${status}, isArrival: ${isArrival}`);
      
      // Generate message first
      const message = generateAnnouncement(flight, status, isArrival);
      
      if (!message) {
        log(`[VOICE] No message generated for ${flight.flightNumber} with status ${status}`);
        handleVoiceEnd();
        return;
      }
      
      log(`[VOICE] Generated message: "${message}"`);
      
      // For CANCELLED status, play shot sound instead of start sound
      // Start voice immediately when sound ends (no delay)
      if (isCancelled && audioShotRef.current) {
        await playAudio(audioShotRef.current);
        
        // Start voice as soon as shot sound ends
        if (message && window.responsiveVoice) {
          log(`[VOICE] Speaking message with ResponsiveVoice (CANCELLED)...`);
          try {
            window.responsiveVoice.speak(message, VOICE_TYPE, {
              rate: 1.0,
              pitch: 1.0,
              volume: 1.0,
              onend: () => {
                log(`[VOICE] ResponsiveVoice ended (CANCELLED)`);
                handleVoiceEnd();
              },
              onerror: (error) => {
                logError(`[VOICE] ResponsiveVoice error (CANCELLED):`, error);
                handleVoiceEnd();
              }
            });
          } catch (error) {
            logError(`[VOICE] Error calling ResponsiveVoice.speak (CANCELLED):`, error);
            handleVoiceEnd();
          }
        } else {
          if (!message) log(`[VOICE] No message to speak (CANCELLED)`);
          if (!window.responsiveVoice) log(`[VOICE] ResponsiveVoice not available (CANCELLED)`);
          handleVoiceEnd();
        }
        return;
      } else if (!isCancelled && audioStartRef.current) {
        // For other statuses, play start sound
        log(`[VOICE] Playing start sound for ${flight.flightNumber}...`);
        
        // Reset audio element to ensure it plays from the beginning
        audioStartRef.current.currentTime = 0;
        audioStartRef.current.pause();
        
        // Reload the audio to ensure it's fresh
        if (audioStartRef.current.readyState === 0 || audioStartRef.current.networkState === 3) {
          log(`[VOICE] Reloading start audio element...`);
          audioStartRef.current.load();
        }
        
        // Wait for audio to be ready before playing
        if (audioStartRef.current.readyState < 2) {
          log(`[VOICE] Waiting for start audio to load (readyState: ${audioStartRef.current.readyState})...`);
          await new Promise((resolve) => {
            const handleCanPlay = () => {
              audioStartRef.current.removeEventListener('canplay', handleCanPlay);
              audioStartRef.current.removeEventListener('error', handleError);
              log(`[VOICE] Start audio is ready to play`);
              resolve();
            };
            const handleError = () => {
              audioStartRef.current.removeEventListener('canplay', handleCanPlay);
              audioStartRef.current.removeEventListener('error', handleError);
              logError(`[VOICE] Start audio failed to load`);
              resolve(); // Continue anyway
            };
            audioStartRef.current.addEventListener('canplay', handleCanPlay, { once: true });
            audioStartRef.current.addEventListener('error', handleError, { once: true });
            // Timeout after 2 seconds
            setTimeout(() => {
              audioStartRef.current.removeEventListener('canplay', handleCanPlay);
              audioStartRef.current.removeEventListener('error', handleError);
              log(`[VOICE] Start audio load timeout, proceeding anyway`);
              resolve();
            }, 2000);
          });
        }
        
        await playAudio(audioStartRef.current);
        log(`[VOICE] Start sound finished, starting voice for ${flight.flightNumber}...`);
        
        // Start voice immediately when start sound ends (no delay)
        if (message && window.responsiveVoice) {
          log(`[VOICE] Speaking message with ResponsiveVoice for ${flight.flightNumber}...`);
          try {
            window.responsiveVoice.speak(message, VOICE_TYPE, {
              rate: 1.0,
              pitch: 1.0,
              volume: 1.0,
              onend: () => {
                log(`[VOICE] ResponsiveVoice ended for ${flight.flightNumber}`);
                handleVoiceEnd();
              },
              onerror: (error) => {
                logError(`[VOICE] ResponsiveVoice error for ${flight.flightNumber}:`, error);
                handleVoiceEnd();
              }
            });
          } catch (error) {
            logError(`[VOICE] Error calling ResponsiveVoice.speak for ${flight.flightNumber}:`, error);
            handleVoiceEnd();
          }
        } else {
          if (!message) log(`[VOICE] No message to speak for ${flight.flightNumber}`);
          if (!window.responsiveVoice) log(`[VOICE] ResponsiveVoice not available for ${flight.flightNumber}`);
          handleVoiceEnd();
        }
        return;
      }

      // Fallback: if no audio files, just speak
      if (message && window.responsiveVoice) {
        await new Promise((resolve) => {
            window.responsiveVoice.speak(message, VOICE_TYPE, {
              rate: 1.0,
              pitch: 1.0,
              volume: 1.0,
              onend: handleVoiceEnd,
              onerror: handleVoiceEnd
            });
        });
      } else {
        handleVoiceEnd();
      }
    } catch (error) {
      logError('Error playing announcement:', error);
      setIsPlaying(false);
      isPlayingRef.current = false;
      if (processNextRef.current) {
        setTimeout(processNextRef.current, 200);
      }
    }
  };

  // Process next announcement in queue (pool system)
  processNextRef.current = () => {
    if (isPlayingRef.current) {
      log(`[VOICE] Already playing, skipping queue processing`);
      return;
    }
    
    if (announcementQueueRef.current.length === 0) {
      log(`[VOICE] Queue is empty`);
      return;
    }

    const announcement = announcementQueueRef.current.shift();
    log(`[VOICE] Processing announcement for ${announcement.flight.flightNumber}, status: ${announcement.status}`);
    
    if (announcement && playAnnouncementRef.current) {
      setIsPlaying(true);
      isPlayingRef.current = true;
      playAnnouncementRef.current(announcement);
    } else {
      log(`[VOICE] No announcement or playAnnouncementRef not available`);
      setIsPlaying(false);
      isPlayingRef.current = false;
    }
  };

  // Detect status changes and queue announcements
  useEffect(() => {
    if (!arrivals || !departures) return;

    // Reset initial load flag if category changed
    if (currentCategoryRef.current !== category) {
      log(`[VOICE] Category changed from ${currentCategoryRef.current} to ${category} - resetting`);
      isInitialLoadRef.current = true;
      currentCategoryRef.current = category;
      // Clear previous flights for new category to avoid false announcements
      previousFlightsRef.current.clear();
      // Clear any queued announcements
      announcementQueueRef.current = [];
      // Clear announced statuses to allow new announcements in new category
      announcedStatusesRef.current.clear();
      // Clear playing state
      setIsPlaying(false);
      isPlayingRef.current = false;
    }

    // On initial load, just record statuses without announcing
    if (isInitialLoadRef.current) {
      log('[VOICE] Initial load - clearing queue and recording statuses without announcing');
      // Clear any queued announcements from previous session
      announcementQueueRef.current = [];
      // Clear announced statuses to start fresh
      announcedStatusesRef.current.clear();
      // Clear playing state
      setIsPlaying(false);
      isPlayingRef.current = false;
      
      // Record all current flight statuses without announcing
      const allFlights = [...arrivals, ...departures];
      allFlights.forEach((flight) => {
        const flightKey = `${flight.flightNumber}-${flight.scheduledTime}`;
        previousFlightsRef.current.set(flightKey, {
          status: flight.status,
          terminal: flight.terminal,
          scheduledTime: flight.scheduledTime,
          isDomestic: flight.isDomestic
        });
      });
      isInitialLoadRef.current = false;
      log(`[VOICE] Initial load complete - recorded ${allFlights.length} flights. Waiting for status changes...`);
      return; // Don't announce on first load
    }

    // Filter flights based on current category (domestic/international)
    const filteredArrivals = arrivals.filter(flight => {
      if (category === 'domestic') {
        return flight.isDomestic === true;
      } else if (category === 'international') {
        return flight.isDomestic === false;
      }
      return true; // Show all if category is not specified
    });

    const filteredDepartures = departures.filter(flight => {
      if (category === 'domestic') {
        return flight.isDomestic === true;
      } else if (category === 'international') {
        return flight.isDomestic === false;
      }
      return true; // Show all if category is not specified
    });

    // Statuses to announce - different for arrivals and departures
    const arrivalsStatusesToAnnounce = ['LANDED', 'DELAYED'];
    const departuresStatusesToAnnounce = ['BOARDING', 'FINAL CALL', 'CANCELLED'];

    // Process arrivals
    filteredArrivals.forEach((flight) => {
      // Use a more stable key that doesn't change with status updates
      const flightKey = `${flight.flightNumber}-${flight.scheduledTime}`;
      const flightKeyAlt = `${flight.flightNumber}-${flight.estimatedTime || flight.scheduledTime}`;
      
      // Try to find previous flight by flight number first, then by key
      let previousFlight = previousFlightsRef.current.get(flightKey);
      if (!previousFlight) {
        previousFlight = previousFlightsRef.current.get(flightKeyAlt);
      }
      
      // If still not found, search by flight number only (in case time changed)
      if (!previousFlight) {
        for (const [key, value] of previousFlightsRef.current.entries()) {
          if (key.startsWith(`${flight.flightNumber}-`)) {
            previousFlight = value;
            // Update the key to use the new scheduledTime
            previousFlightsRef.current.delete(key);
            break;
          }
        }
      }
      
      const currentStatus = flight.status?.toUpperCase() || '';

      // Check if status changed to an announcement-worthy status
      if (previousFlight) {
        const previousStatus = previousFlight.status?.toUpperCase() || '';
        
        // Only announce if status changed AND new status requires announcement for arrivals
        if (previousStatus !== currentStatus && 
            arrivalsStatusesToAnnounce.some(status => currentStatus.includes(status))) {
          
          // Create unique key for this announcement to prevent duplicates
          const announcementKey = `${flight.flightNumber}-${currentStatus}`;
          
          // Check if we've already announced this status for this flight
          if (announcedStatusesRef.current.has(announcementKey)) {
            log(`[VOICE] Already announced ${flight.flightNumber} with status "${currentStatus}" - skipping duplicate`);
            return; // Skip this flight, continue to next
          }
          
          // Mark this announcement as made
          announcedStatusesRef.current.add(announcementKey);
          
          const isCancelled = currentStatus.includes('CANCELLED');
          const isUrgent = currentStatus.includes('FINAL CALL');
          
          // Add to queue (pool system - multiple announcements will play one by one)
          announcementQueueRef.current.push({
            flight,
            status: flight.status,
            isCancelled,
            isUrgent,
            isArrival: true
          });

          log(`[VOICE] Status change detected (Arrival): ${flight.flightNumber} from "${previousStatus}" to "${currentStatus}" - Queued for announcement`);
        }
      }

      // Always update previous flight data with current flight key
      previousFlightsRef.current.set(flightKey, {
        status: flight.status,
        terminal: flight.terminal,
        scheduledTime: flight.scheduledTime,
        isDomestic: flight.isDomestic
      });
    });

    // Process departures
    filteredDepartures.forEach((flight) => {
      // Use a more stable key that doesn't change with status updates
      // Try multiple keys to find the flight if scheduledTime changed
      const flightKey = `${flight.flightNumber}-${flight.scheduledTime}`;
      const flightKeyAlt = `${flight.flightNumber}-${flight.estimatedTime || flight.scheduledTime}`;
      
      // Try to find previous flight by flight number first, then by key
      let previousFlight = previousFlightsRef.current.get(flightKey);
      if (!previousFlight) {
        // Try alternative key
        previousFlight = previousFlightsRef.current.get(flightKeyAlt);
      }
      
      // If still not found, search by flight number only (in case time changed)
      if (!previousFlight) {
        for (const [key, value] of previousFlightsRef.current.entries()) {
          if (key.startsWith(`${flight.flightNumber}-`)) {
            previousFlight = value;
            // Update the key to use the new scheduledTime
            previousFlightsRef.current.delete(key);
            break;
          }
        }
      }
      
      const currentStatus = flight.status?.toUpperCase() || '';

      // Check if status changed to an announcement-worthy status
      if (previousFlight) {
        const previousStatus = previousFlight.status?.toUpperCase() || '';
        
        // Debug logging for status comparison
        if (previousStatus !== currentStatus) {
          log(`[VOICE] Status changed for ${flight.flightNumber}: "${previousStatus}" -> "${currentStatus}"`);
          const shouldAnnounce = departuresStatusesToAnnounce.some(status => currentStatus.includes(status));
          log(`[VOICE] Should announce: ${shouldAnnounce}, checking against:`, departuresStatusesToAnnounce);
          log(`[VOICE] Current status check:`, departuresStatusesToAnnounce.map(s => ({ status: s, matches: currentStatus.includes(s) })));
        }
        
        // Only announce if status changed AND new status requires announcement for departures
        if (previousStatus !== currentStatus && 
            departuresStatusesToAnnounce.some(status => currentStatus.includes(status))) {
          
          // Create unique key for this announcement to prevent duplicates
          const announcementKey = `${flight.flightNumber}-${currentStatus}`;
          
          // Check if we've already announced this status for this flight
          if (announcedStatusesRef.current.has(announcementKey)) {
            log(`[VOICE] Already announced ${flight.flightNumber} with status "${currentStatus}" - skipping duplicate`);
            return; // Skip this flight, continue to next
          }
          
          // Mark this announcement as made
          announcedStatusesRef.current.add(announcementKey);
          
          const isCancelled = currentStatus.includes('CANCELLED');
          const isUrgent = currentStatus.includes('FINAL CALL');
          
          // Add to queue (pool system - multiple announcements will play one by one)
          announcementQueueRef.current.push({
            flight,
            status: flight.status,
            isCancelled,
            isUrgent,
            isArrival: false
          });

          log(`[VOICE] Status change detected (Departure): ${flight.flightNumber} from "${previousStatus}" to "${currentStatus}" - Queued for announcement`);
          log(`[VOICE] Queue length: ${announcementQueueRef.current.length}, isPlaying: ${isPlayingRef.current}`);
        } else if (previousStatus !== currentStatus) {
          log(`[VOICE] Status changed for ${flight.flightNumber} but not queued - new status "${currentStatus}" not in announcement list`);
        }
      } else {
        log(`[VOICE] No previous flight found for ${flight.flightNumber} (first time seeing this flight)`);
      }

      // Always update previous flight data with current flight key
      previousFlightsRef.current.set(flightKey, {
        status: flight.status,
        terminal: flight.terminal,
        scheduledTime: flight.scheduledTime,
        isDomestic: flight.isDomestic
      });
    });

    // Process queue if not already playing
    if (announcementQueueRef.current.length > 0) {
      log(`[VOICE] Queue has ${announcementQueueRef.current.length} announcement(s), isPlaying: ${isPlayingRef.current}`);
        if (!isPlayingRef.current && processNextRef.current) {
          log(`[VOICE] Starting to process queue...`);
          processNextRef.current();
        } else if (isPlayingRef.current) {
          log(`[VOICE] Queue processing deferred - currently playing`);
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivals, departures, category]);

  return { isPlaying, userInteracted };
};

export default useVoiceAnnouncements;
